<template>
  <b-modal
    ref="bModal"
    size="xxl"
    modal-class="incidentModal"
    centered
    qa="incident-form-modal"
    @ok="onOk"
    @hide="onHide"
  >
    <template slot="modal-title">
      {{ incidentLabel }}
    </template>

    <VtLoading v-if="loadingIncident" />
    <information-component
      v-if="coreEntity && (entityIds || coreEntity === COREENTITY_VALUES.GENERAL.code)"
      :information="requestInformation"
      :core-entity="coreEntity"
      :entities-ids="entityIds"
      :entity-information="entityInformation"
      @openModalPatientCard="openModalPatientCard($event)"
    />
    <form class="px-3 py-3 mt-2">
      <b-row class="form-space">
        <b-col>
          <div class="form-card">
            <span class="ap-title ap-title__section">
              <i
                class="far fa-exclamation-triangle mr-2"
                aria-hidden="true"
              />
              {{ $t('component.incidentModal.data') }}
            </span>
            <b-row class="mt-2">
              <b-col
                lg="2"
                md="6"
                sm="4"
              >
                <b-form-group>
                  <div class="form-label">
                    <label
                      :class="{ requiredlabel: (showErrors && !formContent.incidentDate) }"
                    >{{ $t('component.incidentModal.date') }} *</label>
                  </div>
                  <vt-date-picker
                    qa="incident-form-incident-date"
                    :class="{ requiredcalendar: (showErrors && !formContent.incidentDate) }"
                    :disabled="!!(incidentId)"
                    :model="formContent.incidentDate"
                    :not-after="new Date()"
                    @update="formContent.incidentDate = $event"
                  />
                </b-form-group>
              </b-col>
              <b-col
                lg="3"
                md="6"
                sm="8"
              >
                <b-form-group>
                  <div class="form-label">
                    <label
                      :class="{ requiredlabel: (showErrors && !formContent.incidentOrigin) }"
                    >{{ $t('component.incidentModal.area') }} *</label>
                  </div>
                  <vt-combo-select
                    v-model="formContent.incidentOrigin"
                    v-validate="'required'"
                    qa="incident-form-incident-origin-select"
                    :class="{ requiredform: (showErrors && !formContent.incidentOrigin) }"
                    track-by="id"
                    :allow-empty="false"
                    :options="incidentOrigins"
                    label="name"
                    data-vv-name="incidentOrigin"
                    value-key="id"
                    :disabled="!!(incidentId)"
                    @input="getIncidentTypes($event)"
                    @select="onIncidentOriginSelect"
                  />
                </b-form-group>
              </b-col>
              <b-col
                :lg="isRequestCoreEntity"
                md="6"
                sm="8"
              >
                <b-form-group>
                  <div class="form-label">
                    <label
                      :class="{ requiredlabel: (showErrors && !formContent.incidentType) }"
                    >{{ $t('component.incidentModal.type') }} *</label>
                  </div>
                  <vt-combo-select
                    v-model="formContent.incidentType"
                    v-tooltip="incidentTypes.value"
                    v-validate="'required'"
                    qa="incident-form-incident-type-select"
                    :class="{ requiredform: (showErrors && !formContent.incidentType) }"
                    track-by="id"
                    :options="incidentTypes"
                    :allow-empty="false"
                    label="name"
                    data-vv-name="incidentType"
                    value-key="id"
                    :disabled="!formContent.incidentOrigin || !!(incidentId)"
                    @input="onIncidentTypeSelect"
                  />
                </b-form-group>
              </b-col>
              <b-col
                lg="2"
                md="6"
                sm="4"
              >
                <b-form-group>
                  <div class="form-label">
                    <label
                      :class="{ requiredlabel: (showErrors && !formContent.incidentSeverity) }"
                    >{{ $t('component.incidentModal.severity') }} *</label>
                  </div>
                  <vt-combo-select
                    v-model="formContent.incidentSeverity"
                    v-validate="'required'"
                    qa="incident-form-severity-select"
                    :class="{ requiredform: (showErrors && !formContent.incidentSeverity) }"
                    track-by="id"
                    :option-loader="getIncidentSeverities"
                    :allow-empty="false"
                    label="name"
                    data-vv-name="incidentSeverity"
                    :disabled="!!(incidentId) || disableIncidentSeverity"
                  />
                </b-form-group>
              </b-col>
              <b-col
                v-if="coreEntity === 'REQUEST'"
                cols="1"
              >
                <b-form-group
                  :label="$t('component.incidentModal.block')"
                >
                  <div
                    v-tooltip="$tc('registry.viewRequest.blockRequest')"
                    class="col-3 switch-btn p-0"
                  >
                    <input
                      id="toggle"
                      v-model="requestBlocked"
                      :disabled="!canRequestBeBlocked"
                      type="checkbox"
                      class="offscreen"
                      aria-hidden="true"
                    >
                    <label
                      v-testing="'request-blocked-switch'"
                      for="toggle"
                      class="switch"
                    />
                  </div>
                </b-form-group>
              </b-col>
              <b-col cols="12">
                <div class="horizontal-divider mb-3 mt-2" />
              </b-col>
            </b-row>

            <b-row>
              <!--Open section-->
              <b-col
                lg="4"
                md="6"
              >
                <b-form-group class="mb-0">
                  <div class="form-label">
                    <label
                      :class="{ requiredlabel: (showErrors && !formContent.openingObservations) }"
                    >{{ $t('component.incidentModal.open') }} *</label>
                  </div>
                  <label style="width:100%">
                    <textarea
                      v-model="formContent.openingObservations"
                      v-validate="'required'"
                      qa="incident-form-opening-observations-textarea"
                      :class="{ requiredform: (showErrors && !formContent.openingObservations) }"
                      class="form-control"
                      :disabled="!!(incidentId)"
                      data-vv-name="openingObservations"
                      rows="5"
                    />
                  </label>
                </b-form-group>
                <b-row v-if="incidentId">
                  <b-col
                    lg="6"
                    md="12"
                    class="data-col"
                  >
                    <div class="form-label">
                      <label>{{ $t('component.incidentModal.openuser') }}</label>
                    </div>
                    <span
                      v-if="incident && incident.openedBy"
                      class="primary-dark-color"
                    >
                      {{ incident.openedBy.fullName }}
                    </span>
                  </b-col>
                  <b-col
                    lg="6"
                    md="12"
                    class="data-col mb-md-3"
                  >
                    <div class="form-label">
                      <label>{{ $t('component.incidentModal.opendate') }}</label>
                    </div>
                    <span
                      v-if="incident && incident.openingDate"
                      class="primary-dark-color"
                    >
                      {{ incident.openingDate | datetime }}
                    </span>
                  </b-col>
                </b-row>
              </b-col>
              <!--Close section-->
              <b-col
                lg="4"
                md="6"
              >
                <b-form-group class="mb-0">
                  <div class="form-label">
                    <label>{{ $t('component.incidentModal.close') }} {{ incidentId && !closingDate ? '*' : '' }}</label>
                  </div>
                  <label style="width:100%">
                    <textarea
                      v-model="closingObservations"
                      qa="incident-form-closing-observations-textarea"
                      :class="{ requiredform: (showFormCloseErrors && !closingObservations) }"
                      :disabled="!incidentId || closingDate"
                      class="form-control"
                      rows="5"
                    />
                  </label>
                </b-form-group>
                <b-row v-if="incident && incident.closedBy">
                  <b-col
                    cols="5"
                    class="data-col"
                  >
                    <div class="form-label">
                      <label>{{ $t('component.incidentModal.closeuser') }}</label>
                    </div>
                    <span class="primary-dark-color">{{ incident.closedBy.fullName }}</span>
                  </b-col>
                  <b-col
                    cols="5"
                    class="data-col"
                  >
                    <div class="form-label">
                      <label>{{ $t('component.incidentModal.closedate') }}</label>
                    </div>
                    <span class="primary-dark-color">{{ incident.closingDate | datetime }}</span>
                  </b-col>
                </b-row>
              </b-col>
              <!--Attachments-->
              <b-col
                lg="4"
                md="6"
              >
                <b-form-group>
                  <div class="d-flex justify-content-between">
                    <span
                      class="primary-dark-color "
                      style="font-size:.9rem;"
                    >{{ $t('component.incidentModal.attachedFiles') }}</span>
                    <b-button
                      v-if="!incidentId"
                      qa="incident-form-add-attachment-button"
                      class="btn__icon btn__icon--primary btn btn-main btn-secondary"
                      @click="showIncidentAttachmentModal"
                    >
                      <i
                        class="far fa-paperclip"
                        aria-hidden="true"
                      />
                    </b-button>
                  </div>
                  <div
                    v-if="attachments.length"
                    class="file-section"
                  >
                    <span />
                    <div
                      v-for="(file, index) of attachments"
                      :key="file.attachmentId"
                      class="file px-2 py-1 mb-1 d-flex align-items-center justify-content-between"
                    >
                      <span
                        v-if="!file.errorSaving"
                        class="w-100"
                        @click="downloadAttachment(file.attachmentId, index)"
                      >
                        <span>{{ file.file ? file.file.name : file.filename }}</span>
                        <span class="icons d-flex align-items-center">
                          <b-button
                            class="btn__icon btn__icon--primary btn btn-main btn-secondary"
                            style="padding:0"
                          >
                            <i
                              class="far fa-download"
                              style="font-size:1rem;"
                              aria-hidden="true"
                            />
                          </b-button>
                          <b-button
                            v-if="!incidentId"
                            class="ml-1 btn__icon btn__icon--primary-error btn btn-main btn-secondary"
                            style="padding:0"
                            @click="deleteAttachment(file.attachmentId, index)"
                          >
                            <i
                              class="far fa-times"
                              style="font-size:1rem;"
                              aria-hidden="true"
                            />
                          </b-button>
                        </span>
                      </span>
                    </div>
                  </div>
                  <span
                    v-if="!attachments.length"
                    style="font-size:.9rem"
                  >{{ $t('component.incidentModal.noFiles') }}</span>
                </b-form-group>
              </b-col>
            </b-row>
          </div>
        </b-col>
      </b-row>

      <!--Datos de notificación-->

      <b-row class="mt-3">
        <b-col>
          <div class="form-card">
            <span class="ap-title ap-title__section">
              <i
                class="fal fa-envelope mr-2"
                aria-hidden="true"
              />
              {{ $t('component.incidentModal.notifications') }}
            </span>
            <b-row class="mt-2">
              <b-col
                v-if="canNotifyPetitioner"
                cols="3"
                class="py-2 px-3"
              >
                <b-row>
                  <b-col>
                    <div>
                      <b-form-checkbox
                        id="notifyPetitioner"
                        ref="notifyPetitioner"
                        v-model="formContent.notifyRequestingPetitioner"
                        qa="incident-form-notify-requesting-petitioner-checkbox"
                        :disabled="!!(incidentId)"
                        name="notifyPetitioner"
                        :value="true"
                        :unchecked-value="false"
                      >
                        <span style="font-size:.9rem">{{ $t('component.studyRequest.form.clinicCard.petitioner') }}</span>
                      </b-form-checkbox>
                    </div>
                    <div class="mt-1">
                      <b-form-checkbox
                        id="notifyDepartment"
                        ref="notifyDepartment"
                        v-model="formContent.notifyRequestingUnit"
                        qa="incident-form-notifiy-requesting-unit-checkbox"
                        :disabled="!!(incidentId)"
                        name="notifyDepartment"
                        :value="true"
                        :unchecked-value="false"
                      >
                        <span style="font-size:.9rem">{{ $t('component.incidentModal.requestingUnit') }}</span>
                      </b-form-checkbox>
                    </div>
                  </b-col>
                  <b-col
                    cols="1"
                    style="border-left:1px solid #dee2e6"
                  >
                    <div />
                  </b-col>
                </b-row>
              </b-col>
              <b-col>
                <b-row class="multiselection">
                  <b-col>
                    <b-form-group>
                      <div class="form-label">
                        <label>{{ $t('component.incidentModal.serviceResponsibilities') }}</label>
                      </div>
                      <vt-combo-select
                        v-model="selectedResponsibilities"
                        qa="incident-form-responsibilities-select"
                        :class="{ requiredform: (showErrors && (mandatoryNotification && (!selectedPersonnelList.length && !selectedResponsibilities.length))) }"
                        :disabled="!!(incidentId)"
                        :open-direction="'top'"
                        label="label"
                        track-by="label"
                        :option-loader="loadResponsibilities"
                        :multiple="true"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col>
                    <b-form-group>
                      <div class="form-label">
                        <label>{{ $t('component.incidentModal.servicePersonal') }}</label>
                      </div>
                      <!-- TODO: Se han añadido selectores múltiples, dejo comentados los combos anteriores por si fueran necesarios al darle funcionalidad -->
                      <!-- <vt-combo-select v-bind:class="{ requiredform: (showErrors && !formContent.notifiedPersonId) }" v-model="formContent.notifiedPersonId" track-by="id"
                                                                  :option-loader="getPersonnel" label="name"
                                                                  :disabled="!!(incidentId)"
                                                                  tag-placeholder="Add this as new tag"
                                                                  value-key="id"
                                            :multiple="true"/>-->
                      <vt-combo-select
                        v-model="selectedPersonnelList"
                        qa="incident-form-personel-select"
                        :class="{ requiredform: ((showErrors && mandatoryNotification) && (!selectedPersonnelList.length && !selectedResponsibilities.length)) }"
                        :disabled="!!(incidentId)"
                        track-by="id"
                        :option-loader="getPersonnel"
                        label="fullName"
                        :open-direction="'top'"
                        :multiple="true"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
          </div>
        </b-col>
      </b-row>
    </form>
    <modal-wrapper ref="addFileModalWrapper">
      <add-file-modal
        id="addFileModal"
        ref="addFileModal"
        :attachment-entity-type="ATTACHMENT_ENTITY_TYPE"
        :entities-id-array="entitiesIdArray"
        :incident-created="!!incidentId"
        @addAttachmentToNotCreatedIncident="onAddAttachmentToNotCreatedIncident()"
        @attachmentsAdded="onAttachmentsAdded($event)"
      />
    </modal-wrapper>
    <modal-states
      id="statesModalIncidence"
      ref="statesModalIncidence"
    />
    <div
      slot="modal-footer"
      class="footer-banner"
      style="width:100%;"
    >
      <div class="row">
        <div class="col-6 text-left">
          <b-button
            qa="incident-form-cancel-button"
            size="sm"
            variant="outline-secondary"
            data-dismiss="modal"
            @click="hide"
          >
            {{ $t('global.cancel') }}
          </b-button>
        </div>
        <div class="col-6 text-right">
          <vt-loading-button
            v-if="visibleReportButtonButton"
            qa="incident-form-incident-report-button"
            class="btn__outline btn-sm mr-2"
            :params="{ promise: generateIncidentReport }"
          >
            <i
              class="fas fa-print mr-2"
              aria-hidden="true"
            />{{ $t('component.incidentModal.printIncidentReport') }}
          </vt-loading-button>
          <vt-loading-button
            v-if="incidentId && !closingDate"
            :disabled="!canBeClosed"
            qa="incident-form-close-button"
            class="btn__outline btn-sm mr-2"
            :params="{ promise: closeIncident }"
          >
            <i
              class="far fa-times-circle mr-2"
              aria-hidden="true"
            />
            {{ $t('component.incidentModal.closeIncidence') }}
          </vt-loading-button>
          <vt-loading-button
            v-if="!incidentId"
            qa="incident-form-create-incident-button"
            class="btn--primary btn-sm"
            :params="{ promise: createIncident }"
          >
            <i
              class="fal fa-share-square mr-2"
              aria-hidden="true"
            />
            {{ $t('component.incidentModal.register') }}
          </vt-loading-button>
        </div>
      </div>
    </div>
    <print-component ref="printComponent" />
    <modal-wrapper ref="modalPatientCard">
      <modal-patient-card
        v-if="patientId"
        id="modalPatientCard"
        :patient-id="patientId"
      />
    </modal-wrapper>
  </b-modal>
</template>

<script>
import InformationComponent from './InformationComponent'
import AddFileModal from '@/components/AddFileModal'
import ModalStates from '@/components/modals/ModalStates'
import VtLoading from '@/components/common/VtLoading'
import registryRequestResource from '@/modules/RegistryModule/services/registryRequestResource'
import incidentOriginServices from '@/api/resources/incident/incidentOriginServices'
import incidentTypeService from '@/api/resources/incident/incidentTypeService'
import incidentSeverityServices from '@/api/resources/incident/incidentSeverityServices'
import incidentService, { COREENTITY_VALUES } from '@/api/resources/incident/incidentServices'
import incidentAttachmentService from '@/api/resources/attachment/incidentAttachmentService'
import responsibilityService from '@/api/resources/responsibilityService'
import { personnelService } from '@/api/resources/personnelService'
import { REQUEST_STATES } from '@/api/resources/entityState/request-state'
import commonPatientService from '@/api/resources/common/commonPatientServices'
import ModalPatientCard from '@/components/modals/ModalPatientCard'
import { ATTACHMENT_ENTITY_TYPE } from '@/api/resources/attachment/attachmentResource'
import { RequestOperation } from '@/api/operations/models/request-operation'
import { unionBy } from 'lodash'
import { IncidentOperationGuardMixin } from '@/api/operations/mixins/guards/incident-operation-guard.mixin'
import { commonLaboratoryService } from '@/api/resources/common/laboratory/common-laboratory.service'
import { StoreUtils } from '@/shared/util/store-utils'

export default {
    name: 'IncidentFormModal',
    components: {
        InformationComponent, AddFileModal, ModalStates, VtLoading, ModalPatientCard
    },
    mixins: [IncidentOperationGuardMixin],
    props: {
        id: { type: String },
        coreEntity: { type: String, required: true }
    },
    data() {
        return {
            canBeClosed: undefined,
            COREENTITY_VALUES: COREENTITY_VALUES,
            closingDate: null,
            loadingIncident: false,
            mandatoryNotification: false,
            disableIncidentSeverity: false,
            formContent: {
                incidentType: null,
                coreEntity: this.coreEntity,
                incidentOrigin: null,
                openingObservations: '',
                incidentDate: new Date(),
                incidentSeverity: '',
                notifyRequestingUnit: false,
                notifyRequestingPetitioner: false
            },
            incidentTypes: [],
            onOk: () => {
            },
            showErrors: false,
            showFormCloseErrors: false,
            incidentOrigins: [],
            serviceResponsibilities: [],
            requestInformation: null,
            incidentId: null,
            closingObservations: '',
            incident: null,
            requestBlocked: false,
            attachments: [],
            entityIds: null,
            selectedResponsibilities: [],
            selectedPersonnelList: [],
            entityInformation: null,
            patientId: null,
            labId: null,
            responsibilities: [],
            diagnosisCaseGridRouteName: 'diagnosisCaseGrid'
        }
    },
    computed: {
        visibleReportButtonButton() {
            return this.$_canBeIncidentPrinted(this.incident) && this.$_canPrintIncident()
        },
        isCaseLaboratoryResponsibilities() {
            return this.coreEntity === COREENTITY_VALUES.CASE.code ||
                this.coreEntity === COREENTITY_VALUES.SAMPLE.code ||
                this.coreEntity === COREENTITY_VALUES.PORTION.code ||
                this.coreEntity === COREENTITY_VALUES.SUBPORTION.code
        },
        ATTACHMENT_ENTITY_TYPE() {
            return ATTACHMENT_ENTITY_TYPE.INCIDENT
        },
        entitiesIdArray() {
            return [this.labId, this.incidentId]
        },
        incidentLabel() {
            if (this.incident && this.incident.incLabel) {
                return this.incident.incLabel
            } else {
                return this.$t('component.incidentModal.new')
            }
        },
        canNotifyPetitioner() {
            return COREENTITY_VALUES.REQUEST.code === this.coreEntity
        },
        canRequestBeBlocked() {
            return (this.hasOperationInCurrentLaboratoryGlobalMixin(RequestOperation.LOCK) &&
                !this.incidentId && this.entityInformation &&
                this.entityInformation.status.code === REQUEST_STATES.REQUESTED.code)
        },
        isRequestCoreEntity() {
            if (this.coreEntity === 'REQUEST') {
                return '4'
            } else {
                return '5'
            }
        }
    },
    watch: {
        formContent: {
            handler() {
                this.mandatoryNotification = !!(this.formContent.incidentSeverity && this.formContent.incidentSeverity.mandatoryNotification)
            },
            deep: true
        }
    },
    methods: {
        onIncidentTypeSelect(incidentType) {
            this.disableIncidentSeverity = !!(incidentType.incidentSeverity)
            this.formContent.incidentSeverity = incidentType.incidentSeverity
        },
        onIncidentOriginSelect(incidentOrigin) {
            this.selectedPersonnelList = unionBy(this.selectedPersonnelList, incidentOrigin.defaultPersonnelList, 'id')
            this.formContent.incidentType = null
            this.formContent.incidentSeverity = null
            this.disableIncidentSeverity = false
        },
        openModalPatientCard(patientId) {
            this.patientId = patientId
            this.$refs.modalPatientCard.show()
        },
        async loadEntity() {
            let err, response
            switch (this.coreEntity) {
            case COREENTITY_VALUES.PATIENT.code:
                [err, response] = await this.toCall(commonPatientService.getPatientInformation(this.$store.getters.labId, this.entityIds.patId))
                break
            case COREENTITY_VALUES.REQUEST.code:
                [err, response] = await this.toCall(incidentService.getRequestIncidentInformation(this.labId, this.entityIds.requestId))
                break
            case COREENTITY_VALUES.CASE.code:
                [err, response] = await this.toCall(incidentService.getCaseIncidentInformation(this.labId, this.entityIds.casId))
                break
            case COREENTITY_VALUES.SAMPLE.code:
                [err, response] = await this.toCall(incidentService.getSampleIncidentInformation(this.labId, this.entityIds.casId, this.entityIds.samId))
                break
            case COREENTITY_VALUES.PORTION.code:
                [err, response] = await this.toCall(incidentService.getPortionIncidentInformation(this.labId, this.entityIds.casId, this.entityIds.samId, this.entityIds.porId))
                break
            case COREENTITY_VALUES.SUBPORTION.code:
                [err, response] = await this.toCall(incidentService.getSubportionIncidentInformation(this.labId, this.entityIds.casId, this.entityIds.samId, this.entityIds.porId, this.entityIds.supId))
                break
            }
            if (err) {
                this.showErrorModalGlobal()
            }
            this.entityInformation = response
        },
        async deleteAttachment(attId, index) {
            if (attId) {
                let [err] = await this.toCall(
                    incidentAttachmentService.deleteAttachment(
                        this.labId,
                        this.incidentId,
                        attId
                    )
                )
                if (err) {
                    this.showErrorModalGlobal()
                    return
                }
            }
            this.attachments.splice(index, 1)
        },
        async downloadAttachment(attId, index) {
            if (attId) {
                const [err, blob] = await this.toCall(
                    incidentAttachmentService.getFile(this.labId, this.incidentId, attId)
                )
                if (err) {
                    this.showErrorModalGlobal()
                }
                // eslint-disable-next-line no-undef
                saveAs(blob, this.attachments[index].fileName)
            } else {
                // eslint-disable-next-line no-undef
                saveAs(this.attachments[index].file, this.attachments[index].file.name)
            }
        },
        async onAttachmentsAdded(attachments) {
            this.attachments.push(...attachments)
        },
        async onAddAttachmentToNotCreatedIncident() {
            const createRequestResponse = await this.showConfirmationModalGlobal(this.$t('clinical.form.incidentNotCreated'), this.$t('global.save'), this.$t('global.cancel'))
            if (createRequestResponse && await this.createIncident()) {
                this.$nextTick(() => this.$refs.addFileModal.addAttachmentsCall())
            }
        },
        showIncidentAttachmentModal() {
            this.$refs.addFileModalWrapper.show()
        },
        async blockRequest() {
            await registryRequestResource.blockRequest(
                this.labId,
                this.entityIds.requestId
            )
        },
        async validate() {
            return this.$validator.validate()
        },
        hide() {
            this.$refs.bModal.hide()
        },
        async show(detail, entityIds, incidentId, laboratoryId = null) {
            this.requestInformation = detail
            this.entityIds = entityIds
            this.formContent.coreEntity = this.coreEntity
            this.labId = laboratoryId ?? this.$store.getters.labId
            if (incidentId) {
                let err, response
                this.loadingIncident = true
                switch (this.coreEntity) {
                case COREENTITY_VALUES.GENERAL.code:
                    [err, response] = await this.toCall(
                        incidentService.getGeneralIncident(
                            this.labId,
                            incidentId
                        )
                    )
                    break
                case COREENTITY_VALUES.REQUEST.code:
                    [err, response] = await this.toCall(
                        incidentService.getRequestIncident(
                            this.labId,
                            this.entityIds.requestId,
                            incidentId
                        )
                    )
                    break
                case COREENTITY_VALUES.PATIENT.code:
                    [err, response] = await this.toCall(
                        incidentService.getPatientIncident(
                            this.labId,
                            this.entityIds.patId,
                            incidentId
                        )
                    )
                    break
                case COREENTITY_VALUES.CASE.code:
                    [err, response] = await this.toCall(
                        incidentService.getCaseIncident(
                            this.labId,
                            this.entityIds.casId,
                            incidentId
                        )
                    )
                    break
                case COREENTITY_VALUES.SAMPLE.code:
                    [err, response] = await this.toCall(
                        incidentService.getSampleIncident(
                            this.labId,
                            this.entityIds.casId,
                            this.entityIds.samId,
                            incidentId
                        )
                    )
                    break
                case COREENTITY_VALUES.PORTION.code:
                    [err, response] = await this.toCall(
                        incidentService.getPortionIncident(
                            this.labId,
                            this.entityIds.casId,
                            this.entityIds.samId,
                            this.entityIds.porId,
                            incidentId
                        )
                    )
                    break
                case COREENTITY_VALUES.SUBPORTION.code:
                    [err, response] = await this.toCall(
                        incidentService.getSubportionIncident(
                            this.labId,
                            this.entityIds.casId,
                            this.entityIds.samId,
                            this.entityIds.porId,
                            this.entityIds.supId,
                            incidentId
                        )
                    )
                    break
                }
                if (err || !response) {
                    this.loadingIncident = false
                    this.showErrorModalGlobal()
                    this.hide()
                    return
                }
                this.incidentTypes = [response.incidentType]
                this.attachments = response.attachments
                this.formContent = {
                    incidentType: response.incidentType,
                    coreEntity: response.coreEntity.code,
                    incidentOrigin: response.incidentOrigin,
                    openingObservations: response.openingObservations,
                    incidentDate: response.incidentDate,
                    incidentSeverity: response.incidentSeverity,
                    notifyRequestingPetitioner: !!response.notifiedPetitioner,
                    notifyRequestingUnit: !!response.notifiedDepartment
                }
                this.selectedPersonnelList = response.notifiedPersonnelList
                this.selectedResponsibilities = response.notifiedResponsibilityList.map(responsibilityLaboratory => {
                    const responsibility = responsibilityLaboratory.responsibility
                    const laboratory = responsibilityLaboratory.laboratory
                    return {
                        laboratoryId: laboratory.labId,
                        label: `${responsibility.translatedName} (${laboratory.labName})`,
                        code: responsibility.code
                    }
                })
                this.incident = response
                this.closingObservations = response.closingObservations
                this.closingDate = response.closingDate
                this.canBeClosed = response.metadata?.operationGuards?.canBeClosed
                this.incidentId = incidentId
                this.loadingIncident = false
            }
            await this.loadEntity()
            if (COREENTITY_VALUES.REQUEST.code === this.coreEntity && incidentId && this.entityInformation) {
                this.requestBlocked = this.entityInformation.status.code === REQUEST_STATES.LOCKED.code
            } else {
                this.requestBlocked = false
            }
            await this.getIncidentOrigins()
            this.$refs.bModal.show()
        },
        onHide() {
            this.$eventHub.$emit('focus-searcherModule')
            this.$_cleanComponentData()
        },
        async createIncident() {
            this.updateLoading(true)
            const formValidator = await this.validate()
            let mandatoryNotificationValidation = true
            if (this.mandatoryNotification) {
                mandatoryNotificationValidation = this.selectedPersonnelList.length || this.selectedResponsibilities.length
            }
            if (!formValidator || !this.formContent.incidentDate || !mandatoryNotificationValidation) {
                this.showErrors = true
                this.showErrorModalGlobal(this.$t('studyRequest.create.incompleteFormModal.content'))
                this.updateLoading(false)
                return false
            } else {
                this.showErrors = false
                const request = {
                    coreEntity: this.formContent.coreEntity,
                    incidentDate: this.formContent.incidentDate,
                    incidentOriginId: this.formContent.incidentOrigin.id,
                    incidentSeverityId: this.formContent.incidentSeverity.id,
                    incidentTypeId: this.formContent.incidentType.id,
                    notifyRequestingPetitioner: this.formContent.notifyRequestingPetitioner,
                    notifyRequestingUnit: this.formContent.notifyRequestingUnit,
                    openingObservations: this.formContent.openingObservations,
                    notifiedPersonnelIds: this.selectedPersonnelList.map((personnel) => personnel.id),
                    notifiedResponsibilityAndLaboratoryIds: this.selectedResponsibilities
                        .map((responsibility) => {
                            return {
                                laboratoryId: responsibility.laboratoryId,
                                responsibility: responsibility.code
                            }
                        })
                }
                let err, save
                switch (this.coreEntity) {
                case COREENTITY_VALUES.GENERAL.code:
                    [err, save] = await this.toCall(
                        incidentService.openGeneralIncident(
                            this.labId,
                            request
                        )
                    )
                    break
                case COREENTITY_VALUES.REQUEST.code:
                    [err, save] = await this.toCall(
                        incidentService.openRequestIncident(
                            this.labId,
                            this.entityIds.requestId,
                            request
                        )
                    )
                    break
                case COREENTITY_VALUES.PATIENT.code:
                    [err, save] = await this.toCall(
                        incidentService.openPatientIncident(
                            this.labId,
                            this.entityIds.patId,
                            request
                        )
                    )
                    break
                case COREENTITY_VALUES.CASE.code:
                    [err, save] = await this.toCall(
                        incidentService.openCaseIncident(
                            this.labId,
                            this.entityIds.casId,
                            request
                        )
                    )
                    break
                case COREENTITY_VALUES.SAMPLE.code:
                    [err, save] = await this.toCall(
                        incidentService.openSampleIncident(
                            this.labId,
                            this.entityIds.casId,
                            this.entityIds.samId,
                            request
                        )
                    )
                    break
                case COREENTITY_VALUES.PORTION.code:
                    [err, save] = await this.toCall(
                        incidentService.openPortionIncident(
                            this.labId,
                            this.entityIds.casId,
                            this.entityIds.samId,
                            this.entityIds.porId,
                            request
                        )
                    )
                    break
                case COREENTITY_VALUES.SUBPORTION.code:
                    [err, save] = await this.toCall(
                        incidentService.openSubportionIncident(
                            this.labId,
                            this.entityIds.casId,
                            this.entityIds.samId,
                            this.entityIds.porId,
                            this.entityIds.supId,
                            request
                        )
                    )
                    break
                }
                if (err) {
                    this.showErrorModalGlobal()
                    this.updateLoading(false)
                    return false
                }
                if (this.requestBlocked) {
                    await this.blockRequest()
                }
                this.incident = save
                this.canBeClosed = save.metadata?.operationGuards.canBeClosed
                this.incidentId = this.incident.incidentId
                this.showSuccessModalGlobal(this.$t('component.incidentModal.saved'))
                this.updateLoading(false)
                this.$emit('incident-added')
                return true
            }
        },
        async generateIncidentReport() {
            this.updateLoading(true)
            const [err, report] = await this.toCall(incidentService.getIncidentReport(this.labId, this.incidentId, this.coreEntity, this.entityIds))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobalFromErrorResponse(err)
                return
            }
            const filePDF = new Blob([report], { type: 'application/pdf' })
            const fileURL = window.URL.createObjectURL(filePDF)
            window.open(fileURL)
        },
        async getPersonnel() {
            if (this.coreEntity !== COREENTITY_VALUES.PATIENT.code &&
                this.coreEntity !== COREENTITY_VALUES.GENERAL.code &&
                this.coreEntity !== COREENTITY_VALUES.REQUEST.code) {
                return personnelService.getActivePersonnelByLaboratoriesOfCase(this.labId, this.entityIds.casId)
            } else {
                return personnelService.getPersonnel(this.labId, null)
            }
        },
        async getIncidentOrigins() {
            this.incidentOrigins = Object.values(
                await incidentOriginServices.findAllActiveOrigins(this.labId, this.coreEntity)
            ).sort((a, b) => a.name.toLocaleUpperCase().localeCompare(b.name.toLocaleUpperCase()))
        },
        async getIncidentTypes(incidentOrigin) {
            this.incidentTypes = Object.values(
                await incidentTypeService.findAllActiveIncidentTypes(
                    this.labId,
                    incidentOrigin.id,
                    this.coreEntity
                )
            ).sort((a, b) => a.name.toLocaleUpperCase().localeCompare(b.name.toLocaleUpperCase()))
        },
        async getIncidentSeverities() {
            return Object.values(await incidentSeverityServices.getSeverities(this.labId))
        },
        async closeIncident() {
            if (!this.closingObservations) {
                this.showFormCloseErrors = true
                return
            }
            this.showFormCloseErrors = false
            this.updateLoading(true)
            let closeContent = { closingObservations: this.closingObservations }
            let err
            switch (this.coreEntity) {
            case COREENTITY_VALUES.GENERAL.code:
                [err] = await this.toCall(
                    incidentService.closeGeneralIncident(
                        this.labId,
                        this.incidentId,
                        closeContent
                    )
                )
                break
            case COREENTITY_VALUES.REQUEST.code:
                [err] = await this.toCall(
                    incidentService.closeRequestIncident(
                        this.labId,
                        this.entityIds.requestId,
                        this.incidentId,
                        closeContent
                    )
                )
                break
            case COREENTITY_VALUES.PATIENT.code:
                [err] = await this.toCall(
                    incidentService.closePatientIncident(
                        this.labId,
                        this.entityIds.patId,
                        this.incidentId,
                        closeContent
                    )
                )
                break
            case COREENTITY_VALUES.CASE.code:
                [err] = await this.toCall(
                    incidentService.closeCaseIncident(
                        this.labId,
                        this.entityIds.casId,
                        this.incidentId,
                        closeContent
                    )
                )
                break
            case COREENTITY_VALUES.SAMPLE.code:
                [err] = await this.toCall(
                    incidentService.closeSampleIncident(
                        this.labId,
                        this.entityIds.casId,
                        this.entityIds.samId,
                        this.incidentId,
                        closeContent
                    )
                )
                break
            case COREENTITY_VALUES.PORTION.code:
                [err] = await this.toCall(
                    incidentService.closePortionIncident(
                        this.labId,
                        this.entityIds.casId,
                        this.entityIds.samId,
                        this.entityIds.porId,
                        this.incidentId,
                        closeContent
                    )
                )
                break
            case COREENTITY_VALUES.SUBPORTION.code:
                [err] = await this.toCall(
                    incidentService.closeSubportionIncident(
                        this.labId,
                        this.entityIds.casId,
                        this.entityIds.samId,
                        this.entityIds.porId,
                        this.entityIds.supId,
                        this.incidentId,
                        closeContent
                    )
                )
                break
            }
            if (err) {
                this.showErrorModalGlobal()
                this.updateLoading(false)
                return
            }
            this.updateLoading(false)
            this.showSuccessModalGlobal(this.$t('component.incidentModal.closed'))
            this.$emit('incident-closed', this.incident)
            this.hide()
        },
        async getCaseLaboratories() {
            const response = await this.$_toCallUnique(commonLaboratoryService.getCaseLaboratories(this.entityIds?.casId))
            return response ?? []
        },
        mapResponsibilities(responsibilities, laboratories) {
            let responsibilitiesMap = []
            laboratories.forEach(laboratory => {
                responsibilities.forEach(responsibility => {
                    responsibilitiesMap.push({
                        laboratoryId: laboratory.labId,
                        code: responsibility.code,
                        label: `${responsibility.translatedName} (${laboratory.labName})`
                    })
                })
            })
            return responsibilitiesMap
        },
        async loadResponsibilities() {
            let laboratories = []
            if (StoreUtils.selectedLaboratoryConfiguration) {
                laboratories = [StoreUtils.selectedLaboratoryConfiguration]
            }
            if (this.isCaseLaboratoryResponsibilities) {
                laboratories = await this.getCaseLaboratories()
            }
            const responsibilities =
                await this.$_toCallUnique(
                    responsibilityService.findAllResponsibilitiesByLaboratory(this.labId))
            if (responsibilities) {
                return this.mapResponsibilities(responsibilities, laboratories)
                    .sort((a, b) => a.label.localeCompare(b.label))
            }
            return []
        }
    }
}
</script>
<style>
    .incidentModal .multiselect__input,
    .multiselect__single {
        background: transparent !important;
    }

    .incidentModal .multiselection .multiselect__tags {
        height: calc(1.5em + 0.5rem + 70px) !important;
        line-height: 1.5;
        overflow-y: auto;
    }

    .incidentModal .multiselection .multiselect__input {
        margin-bottom: 0;
    }

    .requiredform .multiselect__tags {
        border-color: red !important;
    }

    .requiredform {
        border-color: red !important;
    }

    .requiredcalendar .mx-input {
        border-color: red !important;
    }

    .requiredlabel {
        color: red !important;
    }

    .incidentModal .modal-body {
        padding: 0;
        margin: 0;
        overflow-y: auto;
        overflow-x: hidden;
    }

    .incidentModal #addFileModal .modal-body {
        padding: 3% !important;
    }

    @media only screen and (max-width: 1366px) and (min-width: 1360px) {
        .incidentModal .modal-body {
            max-height: calc(100vh - 180px);
        }

        .incidentModal .modal-dialog {
        top: 5%;
    }
    }
</style>

<style lang="scss" scoped>
    .notification-card .filter-input {
        border: 0;
        padding: 0;
        border-bottom: 1px solid #dee2e6;
        border-radius: 0;
    }

    .notification-card .filter-input:focus {
        border-bottom: 1px solid vt-color(primary);
    }

    .notification-card .filter-input::placeholder {
        color: #c2c2c2;
    }

    .notification-card .box {
        height: 100px;
        overflow: auto;
        overflow-x: hidden;
        padding-top: 5px;
    }

    .notification-card .box ul {
        list-style: none;
        margin: 0;
        padding: 0;
        text-align: left;
    }

    .notification-card .box ul li {
        font-size: 12px;
    }

    .file-section {
        width: 100%;
        height: 115px;
        overflow: hidden;
        overflow-y: auto;
        border-radius: 5px;
    }

    .file-section .file {
        border: 1px solid rgba(0, 0, 0, 0.2);
        width: 100%;
        border-radius: 5px;
        transition: 0.2s;
    }

    .file-section .file:hover {
        border: 1px solid vt-color(primary);
        cursor: pointer;
    }

    .file-section .file:hover span {
        color: vt-color(primary);
    }

    .file-section .file .icons {
        float: right;
        font-size: 1rem;
    }

    .file span {
        color: vt-color(base-text);
        font-size: 13px;
    }

    .errorMessage {
        margin-left: 2%;
        width: 146px;
        height: 15px;
        font-family: 'Roboto', sans-serif;
        font-size: 10px;
        font-weight: normal;
        font-style: normal;
        font-stretch: normal;
        line-height: 1.6;
        letter-spacing: normal;
        color: #fc6274;
    }

    .switch {
        position: relative;
        display: inline-block;
        width: 40px;
        height: 20px;
        background-color: rgba(0, 0, 0, 0.25);
        border-radius: 20px;
        transition: all 0.3s;
    }

    .switch::after {
        content: "";
        position: absolute;
        width: 18px;
        height: 18px;
        border-radius: 18px;
        background-color: white;
        top: 1px;
        left: 1px;
        transition: all 0.3s;
    }

    input[type="checkbox"]:checked + .switch::after {
        transform: translateX(20px);
    }

    input[type="checkbox"]:checked + .switch {
        background-color: #fc6274;
    }

    .offscreen {
        position: absolute;
        left: -9999px;
    }
</style>
